import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Chip,
  useTheme,
  IconButton,
  Tooltip,
  Divider,
  Card,
  CardHeader,
  CardContent,
  TableContainer,
} from "@mui/material";
import ReceiptIcon from "@mui/icons-material/Receipt";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { verEnviarPDFenvasado } from "../../../../services/services";
import moment from "moment";
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import FileUploadEnvasado from "../../fileUpload/FileUploadEnvasado";
import ReactDOM from "react-dom";
import { useState } from "react";

const formatter = (v) =>
  v.toLocaleString("es-AR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

/* Componente para lista de productos */
const parseProduct = (str) => {
  const idRemoved = str.replace(/^ID:\s*\d+\s*-\s*/, "").trim();

  // Extraer cantidad y descripción
  const baseMatch = idRemoved.match(/^(\d+(?:[.,]\d+)?)\s*x\s*(.*?)\s*(\(|$)/i);
  let qty = "";
  let detail = "";

  if (baseMatch) {
    qty = baseMatch[1];
    detail = baseMatch[2].trim();
  }
  // console.log(detail);

  let tamaño = 0;

  // Si el producto es "auto", asignar 15kg
  if (detail.toLowerCase().includes("auto")) {
    tamaño = 15;
  } else {
    // Buscar patrón de kg (ej: 10kg, 15kg, 12KG)
    const tamañoMatch = detail.match(/(\d+(?:[.,]\d+)?)\s*kg/i);
    if (tamañoMatch) {
      tamaño = Number.parseFloat(tamañoMatch[1].replace(",", "."));
    }
  }

  // Extraer precios
  const unitSinIvaMatch = idRemoved.match(/\(Unit\. sin IVA \$([0-9.,]+)\)/i);
  const unitConIvaMatch = idRemoved.match(/\(Unit\. con IVA \$([0-9.,]+)\)/i);
  const totalMatch = idRemoved.match(/\(Total \$([0-9.,]+)\)/i);

  const toNum = (v) => (v ? Number.parseFloat(v.replace(",", ".")) : null);
  const fix = (v) => (v != null ? toNum(v).toFixed(2) : null);

  return {
    qty,
    detail,
    unitSinIVA: fix(unitSinIvaMatch?.[1]),
    unitConIVA: fix(unitConIvaMatch?.[1]),
    total: fix(totalMatch?.[1]),
  };
};

const ordenarProductosPorTamaño = (productos) => {
  if (!productos?.length) return [];

  // Parsear todos los productos
  const productosParseados = productos.map((p) => parseProduct(p));

  // Ordenar por tamaño (kg) del más chico al más grande
  return productosParseados.sort((a, b) => {
    return a.tamaño - b.tamaño;
  });
};

const parseProductos = (productoRenglon) => {
  if (!productoRenglon || typeof productoRenglon !== "string") return [];

  const renglones = productoRenglon.split(" -- ");

  return renglones.map((renglon) => {
    // Patrones regex
    const patterns = {
      id: /ID:\s*(\d+)/,
      cantidad: /(\d+\.?\d*)\s*x\s+/,
      articulo: /\d+\.?\d*\s*x\s+(.+?)\s+\(Unit\./,
      precioSinIva: /Unit\. sin IVA \$(\d+\.?\d*)/,
      precioConIva: /Unit\. con IVA \$(\d+\.?\d*)/,
      totalSinIva: /total sin IVA \$(\d+\.?\d*)/i, // NUEVO: para neto exacto
      total: /Total \$(\d+\.?\d*)/,
    };

    const id = renglon.match(patterns.id)?.[1] || "";
    const cantidad = parseFloat(renglon.match(patterns.cantidad)?.[1] || 0);
    const articulo = renglon.match(patterns.articulo)?.[1]?.trim() || "";
    const precioUnitario = parseFloat(
      renglon.match(patterns.precioSinIva)?.[1] || 0,
    );
    const precioConIva = parseFloat(
      renglon.match(patterns.precioConIva)?.[1] || 0,
    );

    // Usar total sin IVA si existe, sino calcularlo
    const totalSinIva = renglon.match(patterns.totalSinIva)?.[1];
    const neto = totalSinIva
      ? parseFloat(totalSinIva) // Usar el valor exacto del campo
      : precioUnitario * cantidad; // Fallback al cálculo

    const total = parseFloat(renglon.match(patterns.total)?.[1] || 0);
    const iva = total - neto;

    return {
      id,
      cantidad,
      articulo,
      precioUnitario,
      precioConIva,
      neto,
      iva,
      importeConIva: total,
    };
  });
};

/* Envases del pedido que no van en la factura.
   Formato de la query: "id|cantidad|abreviatura|valor_envase -- ..." */
const parseEnvasesNoFacturados = (str) => {
  if (!str) return [];
  return str.split(" -- ").map((item) => {
    const [, cantidad, articulo, valor] = item.split("|");
    const cant = Number(cantidad) || 0;
    const valorEnvase = Number(valor) || 0;
    return { cantidad: cant, articulo, valorEnvase, total: cant * valorEnvase };
  });
};

/* Función para obtener aclaración tal cual viene de la query */
const getAclaracionPromoSena = (str) => {
  if (!str || str === "") return null;
  return str;
};

const PaymentList = ({ pagos, isFactura, c, onOpenFileViewer, pedidoId }) => {
  if (!pagos?.length || !isFactura) return null;

  const pagosCargas = pagos.filter((p) => !p.esEnvase);
  const pagosEnvase = pagos.filter((p) => p.esEnvase);

  return (
    <Box sx={{ mt: 1 }}>
      {/* 🔹 CARGAS */}
      {pagosCargas.length > 0 && (
        <Box sx={{ mb: 1 }}>
          <Typography
            variant="caption"
            sx={{ fontWeight: 600, color: "text.secondary" }}
          >
            Cargas
          </Typography>

          {/* <Box sx={{ mt: 0.5 }}>
            {pagosCargas.map((p, i) => (
              <>
              <Chip
                key={`carga-${i}`}
                // label={`${p.nombre} $${p.monto.toFixed(2)} ${p.detalle !== null ? "(" + p.detalle + ")" : ""}`}
                label={`${p.nombre} $${formatter(p.monto)} ${p.detalle !== null ? "(" + p.detalle + ")" : ""}`}
                size="small"
                variant="outlined"
                color="success"
                sx={{
                  mr: 0.5,
                  mb: 0.5,
                  fontSize: "0.7rem",
                  height: 20,
                }}
              />
              {p.nombre === "Transferencia"?(
                <IconButton
                  // onClick={() => obtenerclientesInactivos()}
                  color="primary"
                  size="small"
                >
                  <RemoveRedEyeIcon />
                </IconButton>
              ):(null)}
              </>
            ))}
          </Box> */}
          <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
  {pagosCargas.map((p, i) => (
    <Chip
      key={`pago-${i}`}
      label={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <span>{`${p.nombre} $${formatter(p.monto)} ${p.detalle !== null ? "(" + p.detalle + ")" : ""}`}</span>
          {p.nombre === "Transferencia" && (
            <RemoveRedEyeIcon 
              sx={{ 
                fontSize: 20,
                cursor: 'pointer',
                '&:hover': { color: 'primary.main' }
              }}
              onClick={(e) => {
      e.stopPropagation();
      if (onOpenFileViewer) {
        onOpenFileViewer(e, c.uuid_pedido); 
      }
    }}
            />
          )}
        </Box>
      }
      size="small"
      variant="outlined"
      color="success"
      sx={{
        fontSize: "0.7rem",
        height: 24, // Aumenta un poco para dar espacio al ícono
      }}
    />
  ))}
</Box>
        </Box>
      )}

      {/* ENVASE */}
      {pagosEnvase.length > 0 && (
        <Box>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            Envases
          </Typography>

          <Box sx={{ mt: 0.5 }}>
            {pagosEnvase.map((p, i) => (
              <Chip
                key={`envase-${i}`}
                // label={`${p.nombre} $${p.monto.toFixed(2)} (${p.detalle})`}
                // label={`${p.nombre} $${formatter(p.monto)} (${p.detalle})`}
                label={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <span>{`${p.nombre} $${formatter(p.monto)} ${p.detalle !== null ? "(" + p.detalle + ")" : ""}`}</span>
          {p.nombre === "Transferencia" && (
            <RemoveRedEyeIcon 
              sx={{ 
                fontSize: 20,
                cursor: 'pointer',
                '&:hover': { color: 'primary.main' }
              }}
              onClick={(e) => {
      e.stopPropagation();
      if (onOpenFileViewer) {
        onOpenFileViewer(e, c.uuid_pedido); 
      }
    }}
            />
          )}
        </Box>
      }
                size="small"
                variant="outlined"
                // color="warning"
                sx={{
                  mr: 0.5,
                  mb: 0.5,
                  fontSize: "0.7rem",
                  height: 20,
                }}
              />
            ))}
          </Box>
          <Box>
            {c.remito_envase && (
              <Typography variant="caption" sx={{ fontWeight: 400 }}>
                Remito Envase {c.remito_envase}
              </Typography>
            )}
          </Box>
          <Box>
            {c.recibo_envase && (
              <Typography variant="caption" sx={{ fontWeight: 400 }}>
                Recibo Envase {c.recibo_envase}
              </Typography>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

/* Función para parsear medios de pago */
const parseMedioPago = (str) => {
  if (!str) return [];

  const matches = [...str.matchAll(/\[([^\]]+)\]/g)];

  return matches.map((m) => {
    const raw = m[1]; // Ej: "Efectivo ($58.00 -- Envase)"

    // Nombre del medio
    const nombre = raw.split(" (")[0].trim();

    // Monto
    const montoMatch = raw.match(/\$\s*([0-9.,]+)/);
    const monto = montoMatch ? Number(montoMatch[1].replace(",", ".")) : 0;

    // Detectar si es envase
    const esEnvase = /envase/i.test(raw);

    // Detalle extra (tarjeta, últimos dígitos, etc)
    const detalleMatch = raw.match(/\$\s*[0-9.,]+\s*--\s*(.*)\)/);
    const detalle = detalleMatch ? detalleMatch[1] : null;

    return {
      nombre,
      monto,
      esEnvase,
      detalle,
      raw,
    };
  });
};

const TablaComprobantes = ({ comprobantes, pagos }) => {
  const theme = useTheme();

  const [fileViewerOpen, setFileViewerOpen] = useState(false);
  const [selectedPedidoId, setSelectedPedidoId] = useState(null);

  const handleOpenFileViewer = (event, pedidoId) => {
    if (event && event.stopPropagation) {
      event.stopPropagation();
      event.preventDefault();
    }
    setSelectedPedidoId(pedidoId);
    setFileViewerOpen(true);
  };

  const handleCloseFileViewer = () => {
    setFileViewerOpen(false);
    setSelectedPedidoId(null);
  };

  // Calcular total de venta solo de FACTURAS
  const totalVenta = comprobantes
    .filter((c) => c.texto_comprobante === "FACTURA" && c.estado !== 328)
    .reduce((sum, c) => sum + c.importe_total, 0);

  const totalCuentaCorriente = comprobantes
    .filter(
      (c) =>
        c.texto_comprobante === "FACTURA" &&
        c.a_cta_cte === 1 &&
        c.estado !== 328,
    )
    .reduce((sum, c) => sum + c.importe_total, 0);

  // const formatter = (v) =>
  //   v.toLocaleString("es-AR", { minimumFractionDigits: 2 });

  return (
    <Paper
      elevation={0}
      sx={{
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        overflow: "hidden",
      }}
    >
      <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Typography
          variant="h6"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            fontWeight: 600,
            color: "text.primary",
          }}
        >
          <ReceiptIcon fontSize="small" />
          Detalle de Comprobantes
        </Typography>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {comprobantes.map((c, i) => {
          // const products = c.producto_renglon
          //   ? c.producto_renglon.split(" -- ")
          //   : [];
          const pagosComprobante = parseMedioPago(c.medios_pago_combinados);
          const isFactura = c.texto_comprobante === "FACTURA";

          // Calcular totales de productos para esta factura
          const productosDetalle = parseProductos(c.producto_fact_item);

          const totalNetoFactura = productosDetalle.reduce(
            (sum, p) => sum + p.neto,
            0,
          );
          const totalIvaFactura = productosDetalle.reduce(
            (sum, p) => sum + p.iva,
            0,
          );

          return (
            <Card key={i} sx={{ mb: 3, position: "relative" }}>
              <CardContent>
                {/* Datos del cliente */}
                {c.estado_factura === 260 ? (
                  <Box>
                    <Typography>
                      <strong>
                        ANULADA [comprobante {c.nc_cod_compr}-{c.nc_pto_venta}-
                        {c.nc_nro_comprobante}]
                      </strong>
                    </Typography>
                  </Box>
                ) : null}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="body2">
                    <strong>
                      {c.texto_comprobante} {c.letra} -{c.pto_venta}-
                      {c.nro_comprobante}
                    </strong>
                    <span
                      style={{ marginLeft: "5px", marginRight: "5px" }}
                    ></span>
                    [
                    {moment(c.fecha_hora_facturacion).format(
                      "DD-MM-YYYY HH:mm",
                    )}
                    ]
                    <span
                      style={{ marginLeft: "25px", marginRight: "25px" }}
                    ></span>
                    <strong>PEDIDO #: {c.id_pedido}</strong> | CAE: {c.cae}
                  </Typography>
                  <Tooltip title="Ver factura" placement="right">
                    <IconButton
                      size="small"
                      onClick={() => verEnviarPDFenvasado(c.id_factura, 0)}
                    >
                      <PictureAsPdfIcon color="secondary" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2">
                    CLIENTE:
                    <strong>
                      {" "}
                      {c.razon_social}{" "}
                      {c.sisven_id !== null ? `(${c.sisven_id})` : null} -
                      C.U.I.T: {c.cuit !== null ? c.cuit : "-"} -{" "}
                      {c.direccion_entrega}
                    </strong>
                  </Typography>

                  {c.texto_comprobante === "FACTURA" && c.a_consfinal === 1 && (
                    <Typography variant="body2" color="text.secondary">
                      A Consumidor Final
                    </Typography>
                  )}
                  {c.texto_comprobante === "FACTURA" && c.a_cta_cte === 1 && (
                    <Typography variant="body2" color="text.secondary">
                      A Cuenta Corriente
                    </Typography>
                  )}
                </Box>

                {/* Tabla de productos */}
                <TableContainer
                  component={Paper}
                  variant="outlined"
                  sx={{ mb: 2 }}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <strong>Cantidad</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Artículo</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>Precio Unit. S/IVA</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>Precio Unit. C/IVA</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>Neto</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>IVA Items</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>Importe con IVA</strong>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {productosDetalle.map((prod, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{prod.cantidad}</TableCell>
                          <TableCell>{prod.articulo}</TableCell>
                          <TableCell align="right">
                            ${formatter(prod.precioUnitario)}
                          </TableCell>
                          <TableCell align="right">
                            ${formatter(prod.precioConIva)}
                          </TableCell>
                          <TableCell align="right">
                            ${formatter(prod.neto)}
                          </TableCell>
                          <TableCell align="right">
                            ${formatter(prod.iva)}
                          </TableCell>
                          <TableCell align="right">
                            ${formatter(prod.importeConIva)}
                          </TableCell>
                        </TableRow>
                      ))}

                      {/* Totales de la factura */}
                      <TableRow
                        sx={{
                          bgcolor: "grey.50",
                          borderTop: 2,
                          borderColor: "divider",
                        }}
                      >
                        <TableCell colSpan={4} align="right">
                          {/* <strong>TOTALES:</strong> */}
                        </TableCell>
                        <TableCell align="right">
                          <strong>${formatter(totalNetoFactura)}</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>${formatter(totalIvaFactura)}</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>
                            ${formatter(totalNetoFactura + totalIvaFactura)}
                          </strong>
                        </TableCell>
                      </TableRow>

                      {/* Percepciones y tributos */}
                      {(c.importe_perc_iva > 0 ||
                        c.importe_percepciones > 0) && (
                        <>
                          {c.importe_perc_iva > 0 && (
                            <TableRow>
                              <TableCell align="right"></TableCell>
                              <TableCell align="right"></TableCell>
                              <TableCell colSpan={4} align="right">
                                Perc. IVA:
                              </TableCell>
                              <TableCell align="right" colSpan={1}>
                                <strong>
                                  ${formatter(c.importe_perc_iva)}
                                </strong>
                              </TableCell>
                            </TableRow>
                          )}
                          {c.importe_percepciones > 0 && (
                            <TableRow>
                              <TableCell align="right"></TableCell>
                              <TableCell align="right"></TableCell>
                              <TableCell colSpan={4} align="right">
                                Otros Tributos:
                              </TableCell>
                              <TableCell align="right" colSpan={1}>
                                <strong>
                                  ${formatter(c.importe_percepciones)}
                                </strong>
                              </TableCell>
                            </TableRow>
                          )}
                        </>
                      )}
                      <TableRow>
                        <TableCell align="right"></TableCell>
                        <TableCell align="right"></TableCell>
                        <TableCell colSpan={4} align="right">
                          Total:
                        </TableCell>
                        <TableCell align="right" colSpan={1}>
                          <strong>${formatter(c.importe_total)}</strong>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>

{/* Envases del pedido que no están en la factura */}
{(() => {
  const envases = parseEnvasesNoFacturados(c.envases_no_facturados);
  if (envases.length === 0) return null;

  const totalEnvasesPedido = envases.reduce((sum, e) => sum + e.total, 0);

  return (
    <Box sx={{
      mb: 2,
      p: 1.5,
      borderRadius: 1,
      border: `1px solid ${theme.palette.warning.main}`,
      backgroundColor: theme.palette.warning.light + '15'
    }}>
      <Typography variant="caption" sx={{ fontWeight: 600, color: 'warning.dark', display: 'block', mb: 1 }}>
        Envases (no incluidos en la factura)
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell><strong>Cantidad</strong></TableCell>
            <TableCell><strong>Artículo</strong></TableCell>
            <TableCell align="right"><strong>Valor Unit.</strong></TableCell>
            <TableCell align="right"><strong>Total</strong></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {envases.map((e, idx) => (
            <TableRow key={idx}>
              <TableCell>{e.cantidad}</TableCell>
              <TableCell>{e.articulo}</TableCell>
              <TableCell align="right">${formatter(e.valorEnvase)}</TableCell>
              <TableCell align="right">${formatter(e.total)}</TableCell>
            </TableRow>
          ))}
          <TableRow>
            <TableCell colSpan={3} align="right">Total Envases:</TableCell>
            <TableCell align="right"><strong>${formatter(totalEnvasesPedido)}</strong></TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </Box>
  );
})()}

{/* Aclaración de Promociones y/o Señales */}
{(() => {
  const aclaracion = getAclaracionPromoSena(c.aclaracion_promo_sena);
  if (!aclaracion) return null;
  
  return (
    <Box sx={{ 
      mb: 2, 
      p: 1.5, 
      borderRadius: 1,
      border: `1px solid ${theme.palette.warning.main}`,
      backgroundColor: theme.palette.warning.light + '15'
    }}>
      {/* <Typography variant="caption" sx={{ fontWeight: 600, color: 'warning.dark', display: 'block', mb: 1 }}>
        ⚠️ RENGLONES CON PROMOCIÓN/SEÑA
      </Typography> */}
      <Typography 
        variant="body2" 
        sx={{ 
          fontSize: '0.75rem', 
          color: 'text.secondary',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word'
        }}
      >
        {aclaracion}
      </Typography>
    </Box>
  );
})()}
                {/* Medios de pago */}
                <PaymentList
                  pagos={pagosComprobante}
                  isFactura={isFactura}
                  c={c}
                  onOpenFileViewer={handleOpenFileViewer}  
                  pedidoId={c.id_pedido}   
                />
              </CardContent>
            </Card>
          );
        })}

        {/* ========== SECCIÓN DE TOTALES EN CARDS ========== */}
{/* ========== SECCIÓN DE TOTALES EN CARDS ========== */}
{pagos && pagos.length > 0 && (
  <Box sx={{ mt: 3, display: "flex", flexDirection: "column", gap: 2 }}>
    {/* Card: Medios de Pago - Cargas */}
    {(() => {
      const pagosGas = pagos.filter((p) => p.tipo_pago === "Gas");
      if (pagosGas.length === 0) return null;

      return (
        <Card variant="outlined">
          <CardHeader
            title="Medios de Pago - Cargas"
            titleTypographyProps={{ variant: "subtitle2", color: "text.secondary" }}
            sx={{ pb: 0 }}
          />
          <CardContent>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {pagosGas.map((p, i) => (
                <Box
                  key={i}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {p.nombre_medio}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    ${formatter(p.total)}
                  </Typography>
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>
      );
    })()}

    {/* Card: Cuenta Corriente */}
    {totalCuentaCorriente > 0 &&
      !pagos.some((p) => p.nombre_medio === "Cuenta Corriente") && (
        <Card variant="outlined">
          <CardContent>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="subtitle2" color="text.secondary">
                Cuenta Corriente
              </Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                ${formatter(totalCuentaCorriente)}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}

    {/* Card: Total Facturas */}
    <Card variant="outlined" sx={{ bgcolor: "grey.50" }}>
      <CardContent>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Total Facturas
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            ${formatter(totalVenta)}
          </Typography>
        </Box>
      </CardContent>
    </Card>

    {/* Card: Medios de Pago - Envases */}
    {(() => {
      const pagosEnvase = pagos.filter((p) => p.tipo_pago === "Envase");
      if (pagosEnvase.length === 0) return null;

      const totalEnvases = pagosEnvase.reduce((sum, p) => sum + p.total, 0);

      return (
        <Card variant="outlined" sx={{ borderColor: "warning.main" }}>
          <CardHeader
            title="Medios de Pago - Envases"
            titleTypographyProps={{ variant: "subtitle2", color: "warning.main" }}
            sx={{ pb: 0 }}
          />
          <CardContent>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {pagosEnvase.map((p, i) => (
                <Box
                  key={i}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {p.nombre_medio}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    ${formatter(p.total)}
                  </Typography>
                </Box>
              ))}
              <Divider sx={{ my: 0.5 }} />
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="subtitle2" color="warning.main">
                  Total Envases
                </Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "warning.main" }}>
                  ${formatter(totalEnvases)}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      );
    })()}
    </Box>
    )}
    </Box>

    {/* PORTAL: Renderizar el modal fuera del árbol de React */}
{fileViewerOpen && ReactDOM.createPortal(
  <div 
    onClick={(e) => e.stopPropagation()}
    style={{ 
      position: 'fixed', 
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 9999,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      pointerEvents: 'none'
    }}
  >
    <div style={{ pointerEvents: 'auto' }}>
      <FileUploadEnvasado
        open={fileViewerOpen}
        fileId={selectedPedidoId}
        onClose={handleCloseFileViewer}
        canEdit={false}
        title="Comprobantes de Transferencia"
      />
    </div>
  </div>,
  document.body
)}
    </Paper>
  );
};

export default TablaComprobantes;
